// [SUPERADMIN-1-STATS] - Page Paramètres Super Admin
import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Settings,
  Shield,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Save,
  AlertTriangle,
  Server,
  Database,
  Globe
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { COLORS } from "../components/SuperAdminLayout";

export default function SuperAdminSettings() {
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    email: "admin@boutiqueos.com",
    password: "SuperAdmin2026!",
    platformName: "BoutiqueOS",
    maintenanceMode: false
  });

  const handleSave = () => {
    // Simuler la sauvegarde
    toast.success("Paramètres enregistrés avec succès !");
  };

  return (
    <div className="space-y-6" data-testid="superadmin-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: COLORS.text }}>Paramètres</h1>
        <p className="text-sm" style={{ color: COLORS.textSecondary }}>
          Configuration de la plateforme Super Admin
        </p>
      </div>

      {/* Identifiants Super Admin */}
      <Card 
        className="p-6"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${COLORS.accent}15` }}
          >
            <Shield className="w-5 h-5" style={{ color: COLORS.accent }} />
          </div>
          <div>
            <h2 className="font-semibold" style={{ color: COLORS.text }}>Identifiants Super Admin</h2>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>
              Modifiez vos informations de connexion
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: COLORS.textSecondary }} />
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl focus:outline-none"
                style={{ backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              />
            </div>
          </div>

          {/* Mot de passe */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>Mot de passe</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: COLORS.textSecondary }} />
              <input
                type={showPassword ? "text" : "password"}
                value={settings.password}
                onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                className="w-full pl-10 pr-12 py-2.5 rounded-xl focus:outline-none"
                style={{ backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: COLORS.textSecondary }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
              Note : Les identifiants sont stockés dans le code (SuperAdminLogin.jsx)
            </p>
          </div>
        </div>
      </Card>

      {/* Configuration Plateforme */}
      <Card 
        className="p-6"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${COLORS.accent}15` }}
          >
            <Globe className="w-5 h-5" style={{ color: COLORS.accent }} />
          </div>
          <div>
            <h2 className="font-semibold" style={{ color: COLORS.text }}>Configuration Plateforme</h2>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>
              Paramètres généraux de BoutiqueOS
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Nom plateforme */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.text }}>Nom de la plateforme</label>
            <input
              type="text"
              value={settings.platformName}
              onChange={(e) => setSettings({ ...settings, platformName: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl focus:outline-none"
              style={{ backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
            />
          </div>

          {/* Mode maintenance */}
          <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: COLORS.bg }}>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5" style={{ color: COLORS.warning }} />
              <div>
                <p className="font-medium" style={{ color: COLORS.text }}>Mode maintenance</p>
                <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                  Bloquer l'accès aux boutiques temporairement
                </p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, maintenanceMode: !settings.maintenanceMode })}
              className={`w-12 h-6 rounded-full transition-colors relative`}
              style={{ backgroundColor: settings.maintenanceMode ? COLORS.accent : COLORS.border }}
            >
              <div 
                className="absolute top-1 w-4 h-4 rounded-full bg-white transition-transform"
                style={{ left: settings.maintenanceMode ? '26px' : '4px' }}
              />
            </button>
          </div>
        </div>
      </Card>

      {/* Infos système */}
      <Card 
        className="p-6"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${COLORS.accent}15` }}
          >
            <Server className="w-5 h-5" style={{ color: COLORS.accent }} />
          </div>
          <div>
            <h2 className="font-semibold" style={{ color: COLORS.text }}>Informations Système</h2>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>
              État de l'infrastructure
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.bg }}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.positive }} />
              <span className="text-sm" style={{ color: COLORS.textSecondary }}>Serveur</span>
            </div>
            <p className="font-semibold" style={{ color: COLORS.text }}>Opérationnel</p>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.bg }}>
            <div className="flex items-center gap-2 mb-1">
              <Database className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
              <span className="text-sm" style={{ color: COLORS.textSecondary }}>Base de données</span>
            </div>
            <p className="font-semibold" style={{ color: COLORS.text }}>MongoDB</p>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.bg }}>
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
              <span className="text-sm" style={{ color: COLORS.textSecondary }}>Version</span>
            </div>
            <p className="font-semibold" style={{ color: COLORS.text }}>v2.0.0</p>
          </div>
        </div>
      </Card>

      {/* Bouton sauvegarder */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          className="gap-2"
          style={{ backgroundColor: COLORS.accent }}
        >
          <Save className="w-4 h-4" />
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
}
